// /my/ header — mobile submenu accordion.
//
// Ported from epos_com zippy-child (assets/js/header-my.js). The site-wide
// store-CTA boot (initStoreCta) is intentionally omitted — the academy runs
// "core chrome only", so the Get Started pill keeps its static JSON href.
//
// `wp_nav_menu` renders parent items as:
//   <li class="menu-item-has-children"><a>Label</a><ul class="sub-menu">…</ul></li>
//
// The arrow lives inside the <a>, so we cannot separate "tap text = navigate"
// from "tap arrow = expand" with CSS alone. This module injects a dedicated
// toggle button after each parent link: tapping the text still follows the
// link, while tapping the arrow / rest of the row toggles the submenu open.
//
// For a smooth accordion we measure each submenu's natural height in JS and
// store it as an inline `max-height`. A default `is-collapsed` class forces
// `max-height: 0 !important`; removing it lets the transition animate to the
// exact predefined height instead of an arbitrary large value.
//
// Measurement is deferred until the user first opens a submenu so no layout
// work happens during page load.

(function () {
  "use strict";

  // Drawer breakpoint mirrors _header-my.scss (`max-width: 1120px`). Above it
  // the accordion is `display: none`, so measuring submenus would only churn
  // layout for no visual benefit.
  var DRAWER_MQ =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 1120px)")
      : null;

  function isDrawerActive() {
    return DRAWER_MQ ? DRAWER_MQ.matches : true;
  }

  // Measure a submenu's natural expanded height. Called lazily on the first
  // open so it never runs during page load. The `is-collapsed` class zeroes
  // padding as well as max-height, so it must be removed before measuring or
  // the target height will be short by the padding.
  //
  // The result is cached in a WeakMap keyed by the sub-menu element. The
  // cache is invalidated on resize (fonts/images can shift heights).
  var heightCache = typeof WeakMap === "function" ? new WeakMap() : null;

  function measureSubmenu(sub) {
    if (heightCache && heightCache.has(sub)) return heightCache.get(sub);
    var wasCollapsed = sub.classList.contains("is-collapsed");
    if (wasCollapsed) sub.classList.remove("is-collapsed");
    var h = sub.scrollHeight;
    if (wasCollapsed) sub.classList.add("is-collapsed");
    if (heightCache) heightCache.set(sub, h);
    return h;
  }

  function initHeaderMyAccordion() {
    var header = document.querySelector(".header-my");
    if (!header) return;

    var parents = header.querySelectorAll(
      ".header-my__menu > li.menu-item-has-children"
    );

    Array.prototype.forEach.call(parents, function (li) {
      // Skip if already initialised.
      if (li.querySelector(".header-my__submenu-toggle")) return;

      var link = li.querySelector("a");
      if (!link) return;

      var sub = li.querySelector(".sub-menu");
      if (sub) sub.classList.add("is-collapsed"); // collapsed by default

      var toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "header-my__submenu-toggle";
      toggle.setAttribute("aria-label", "Toggle submenu");
      toggle.setAttribute("aria-expanded", "false");

      link.insertAdjacentElement("afterend", toggle);

      toggle.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var isOpen = li.classList.toggle("is-open");
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
        if (!sub) return;

        if (isOpen) {
          // Lazy measure — this is the single reflow point, on user intent.
          // Setting inline max-height before removing the class ensures the
          // computed value changes from 0 (blocked by !important) to the
          // measured height, which triggers the CSS transition.
          sub.style.maxHeight = measureSubmenu(sub) + "px";
          sub.classList.remove("is-collapsed");
        } else {
          sub.classList.add("is-collapsed");
        }
      });
    });
  }

  // Hide the sticky header while scrolling down, reveal it while scrolling up.
  //
  // The header keeps `position: sticky` (see _header-my.scss); we only toggle
  // an `.is-hidden` class that slides it above the viewport via
  // `transform: translateY(-100%)`. Direction is derived from the scroll delta,
  // throttled with requestAnimationFrame and gated by a small threshold so
  // 1px jitter never flips the state. Near the top of the page the header is
  // always shown, and it is never hidden while a mobile submenu is open (that
  // would slide the open drawer off-screen).
  function initHeaderScrollReveal() {
    var header = document.querySelector(".header-my");
    if (!header) return;

    var SCROLL_THRESHOLD = 8; // px of accumulated movement before a flip
    var TOP_OFFSET = 80; // always visible within this distance from the top
    var lastY = window.pageYOffset;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.pageYOffset;
      var delta = y - lastY;

      // Close to the top: pin the header visible and reset the anchor.
      if (y <= TOP_OFFSET) {
        header.classList.remove("is-hidden");
        lastY = y;
        return;
      }

      // Ignore sub-threshold movement but keep accumulating from the anchor.
      if (Math.abs(delta) < SCROLL_THRESHOLD) return;

      if (delta > 0) {
        // Scrolling down — hide unless a mobile submenu is currently open.
        var submenuOpen =
          isDrawerActive() &&
          header.querySelector(".header-my__menu > li.is-open");
        if (!submenuOpen) header.classList.add("is-hidden");
      } else {
        // Scrolling up — always reveal.
        header.classList.remove("is-hidden");
      }
      lastY = y;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
  }

  function onReady() {
    initHeaderMyAccordion();
    initHeaderScrollReveal();
  }

  if (document.readyState !== "loading") {
    onReady();
  } else {
    document.addEventListener("DOMContentLoaded", onReady);
  }

  // Invalidate cached heights on resize (fonts loading, orientation change,
  // window resize can all shift submenu content height). Debounced so a drag
  // resize doesn't clear the cache on every pixel.
  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (!isDrawerActive()) return;
      // Reset cache; next open will re-measure.
      heightCache = typeof WeakMap === "function" ? new WeakMap() : null;
    }, 150);
  });
})();
